"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useFormStatus } from "react-dom";
import { sendMessageAction } from "@frontend/lib/actions";
import { clientApi, publicApiUrl } from "@frontend/lib/api";
import { parseSystemEvent } from "@frontend/lib/system-events";
import type { Message } from "@creators/backend/db/schema";
import { dayKey, formatDateTime, formatDayLabel, formatTime } from "@creators/backend/lib/time";

const POLL_MS = 3_000;
const STALE_AFTER_FAILURES = 3;

type MessageRow = Message;

function mergeMessages(current: MessageRow[], incoming: MessageRow[]): MessageRow[] {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((m) => m.id));
  const merged = [...current];
  for (const message of incoming) {
    if (!seen.has(message.id)) merged.push(message);
  }
  return merged;
}

function latestTimestamp(rows: MessageRow[]): string | null {
  const last = rows.at(-1);
  if (!last) return null;
  return new Date(last.createdAt).toISOString();
}

function isNearBottom(el: HTMLElement, threshold = 96): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
}

export function Thread({
  collabId,
  messages: initialMessages,
  viewer,
  creatorName,
  brandName,
  canPost,
  showReadReceipts = false,
  counterpartLastReadAt: initialCounterpartLastReadAt = null,
}: {
  collabId: string;
  messages: MessageRow[];
  viewer: "creator" | "brand" | "admin";
  creatorName: string;
  brandName: string;
  canPost: boolean;
  /** Priority boosts only — Seen under your last message the other side opened. */
  showReadReceipts?: boolean;
  counterpartLastReadAt?: Date | string | null;
}) {
  const [messages, setMessages] = useState(initialMessages);
  const [counterpartLastReadAt, setCounterpartLastReadAt] = useState<Date | null>(() =>
    initialCounterpartLastReadAt ? new Date(initialCounterpartLastReadAt) : null,
  );
  const [stale, setStale] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  const stickToBottomRef = useRef(true);
  const didMountRef = useRef(false);
  const failuresRef = useRef(0);
  const inFlightRef = useRef(false);
  const partyNames = { creator: creatorName, brand: brandName };

  messagesRef.current = messages;

  const showReadReceiptsRef = useRef(showReadReceipts);
  showReadReceiptsRef.current = showReadReceipts;

  const syncMessages = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const since = latestTimestamp(messagesRef.current);
    const query = since ? `?since=${encodeURIComponent(since)}` : "";
    try {
      const data = await clientApi<{
        messages: MessageRow[];
        counterpartLastReadAt?: Date | string | null;
      }>(`/api/collabs/${collabId}/messages${query}`);
      failuresRef.current = 0;
      setStale(false);
      setMessages((current) => mergeMessages(current, data.messages));
      if (showReadReceiptsRef.current && data.counterpartLastReadAt != null) {
        const next = new Date(data.counterpartLastReadAt);
        if (!Number.isNaN(next.getTime())) {
          setCounterpartLastReadAt((prev) =>
            !prev || next.getTime() >= prev.getTime() ? next : prev,
          );
        }
      }
    } catch {
      failuresRef.current += 1;
      if (failuresRef.current >= STALE_AFTER_FAILURES) setStale(true);
    } finally {
      inFlightRef.current = false;
    }
  }, [collabId]);

  // Soft room refreshes pass a new array reference with the same tip — do not
  // wipe locally polled messages or remount the scroller every few seconds.
  const seedSig = `${initialMessages.length}:${initialMessages.at(-1)?.id ?? ""}`;
  useEffect(() => {
    setMessages((current) => {
      if (current.length === 0) return initialMessages;
      const tip = initialMessages.at(-1)?.id;
      if (tip && current.some((row) => row.id === tip)) {
        return mergeMessages(initialMessages, current);
      }
      return initialMessages;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSig]);

  useEffect(() => {
    if (!initialCounterpartLastReadAt) return;
    const next = new Date(initialCounterpartLastReadAt);
    if (Number.isNaN(next.getTime())) return;
    setCounterpartLastReadAt((prev) =>
      !prev || next.getTime() >= prev.getTime() ? next : prev,
    );
  }, [initialCounterpartLastReadAt]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") void syncMessages();
    };
    const id = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [syncMessages]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (!didMountRef.current) {
      didMountRef.current = true;
      el.scrollTop = el.scrollHeight;
      return;
    }

    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = isNearBottom(el);
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Absolute scroller so the message count never dictates the pane's height. */}
      <div className="relative min-h-0 flex-1">
        {stale ? <ReconnectingBanner /> : null}

        <div
          ref={listRef}
          onScroll={handleListScroll}
          className="absolute inset-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
        >
          {/* A short thread rests on the composer rather than floating at the top. */}
          <ol className="flex min-h-full flex-col justify-end px-4 py-4 lg:px-6">
            {messages.length === 0 ? (
              <li className="flex flex-1 items-center justify-center px-5 py-12 text-center text-sm text-ink-muted">
                No messages yet — say hello.
              </li>
            ) : (
              (() => {
                let lastMineId: string | null = null;
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i]!.sender === viewer) {
                    lastMineId = messages[i]!.id;
                    break;
                  }
                }
                return messages.map((message, index) => {
                const previous = index > 0 ? messages[index - 1] : null;
                const next = index < messages.length - 1 ? messages[index + 1] : null;
                const newDay =
                  !previous || dayKey(previous.createdAt) !== dayKey(message.createdAt);
                const nextNewDay = next
                  ? dayKey(next.createdAt) !== dayKey(message.createdAt)
                  : true;
                const mine = message.sender === viewer;
                const lastMine = mine && message.id === lastMineId;
                const readByCounterpart =
                  showReadReceipts &&
                  mine &&
                  counterpartLastReadAt != null &&
                  new Date(message.createdAt).getTime() <= counterpartLastReadAt.getTime();

                return (
                  <Fragment key={message.id}>
                    {newDay ? <DaySeparator at={message.createdAt} /> : null}
                    {message.sender === "platform" ? (
                      <ActivityLine
                        collabId={collabId}
                        message={message}
                        names={partyNames}
                        connectTop={!newDay && previous?.sender === "platform"}
                        connectBottom={!nextNewDay && next?.sender === "platform"}
                      />
                    ) : (
                      <ChatBubble
                        collabId={collabId}
                        message={message}
                        mine={mine}
                        author={message.sender === "creator" ? creatorName : brandName}
                        showReadReceipts={showReadReceipts && mine}
                        read={readByCounterpart}
                        showSeenLabel={Boolean(lastMine && readByCounterpart)}
                      />
                    )}
                  </Fragment>
                );
                });
              })()
            )}
          </ol>
        </div>
      </div>

      {canPost ? (
        <div className="shrink-0 border-t border-line bg-surface px-3 py-3 lg:px-6">
          <MessageForm collabId={collabId} onSent={syncMessages} />
        </div>
      ) : null}
    </section>
  );
}

function ReconnectingBanner() {
  return (
    <div
      role="status"
      className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-50 px-2.5 py-1 text-[11px] font-medium text-warn-700 shadow-sm ring-1 ring-warn-100">
        <span className="size-1.5 animate-pulse rounded-full bg-warn-600" aria-hidden="true" />
        Reconnecting…
      </span>
    </div>
  );
}

function MessageForm({ collabId, onSent }: { collabId: string; onSent: () => Promise<void> }) {
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A failed send keeps what was typed, so the composer is only cleared once the
  // message is actually on the record.
  const send = async (formData: FormData) => {
    const result = await sendMessageAction({}, formData);
    setError(result.error ?? null);
    if (result.ok) {
      formRef.current?.reset();
      setSelectedFile(null);
      if (textareaRef.current) textareaRef.current.style.height = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    if (result.ok || result.error) await onSent();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    const body = textareaRef.current?.value.trim();
    if (!body && !selectedFile) return;
    formRef.current?.requestSubmit();
  };

  return (
    <form ref={formRef} action={send}>
      <input type="hidden" name="collabId" value={collabId} />

      {selectedFile ? (
        <div className="mb-2 flex items-center gap-2 px-1">
          <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-canvas px-2.5 py-1 text-xs text-ink-muted ring-1 ring-line">
            <PaperclipIcon className="size-3.5 shrink-0" />
            <span className="truncate">{selectedFile.name}</span>
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="shrink-0 text-ink-subtle hover:text-ink"
              aria-label="Remove attachment"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center rounded-3xl border border-line bg-canvas/50 py-1 pl-1.5 pr-2 transition focus-within:border-brand-500 focus-within:bg-surface">
          <input
            ref={fileInputRef}
            type="file"
            name="attachment"
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
            className="sr-only"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0] ?? null;
              setSelectedFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach a file"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-subtle transition hover:bg-canvas hover:text-ink"
          >
            <PaperclipIcon className="size-[18px]" />
          </button>
          <textarea
            ref={textareaRef}
            name="body"
            rows={1}
            placeholder="Your message"
            onKeyDown={handleKeyDown}
            onInput={(event) => {
              const el = event.currentTarget;
              el.style.height = "";
              el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
            }}
            className="max-h-[7.5rem] min-h-9 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-base leading-normal text-ink outline-none placeholder:text-ink-subtle"
          />
        </div>
        <SendButton />
      </div>

      {error ? <p className="mt-1.5 px-1 text-[11px] leading-snug text-warn-700">{error}</p> : null}
    </form>
  );
}

function SendButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      aria-label="Send message"
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-canvas shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
    >
      {pending ? (
        <span className="text-sm leading-none">…</span>
      ) : (
        <SendIcon className="size-[18px]" />
      )}
    </button>
  );
}

function DaySeparator({ at }: { at: Date | string }) {
  return (
    <li className="flex justify-center py-3" aria-hidden="true">
      <span className="rounded-full bg-canvas px-2.5 py-1 text-[10px] font-semibold tracking-wide text-ink-subtle uppercase ring-1 ring-line/70">
        {formatDayLabel(at)}
      </span>
    </li>
  );
}

/** Only money and trouble earn colour; everything else stays quiet. */
const DOT_TONES: Record<string, string> = {
  money: "bg-money-600",
  warn: "bg-alert-600",
};

function ActivityLine({
  collabId,
  message,
  names,
  connectTop,
  connectBottom,
}: {
  collabId: string;
  message: MessageRow;
  names: { creator: string; brand: string };
  connectTop?: boolean;
  connectBottom?: boolean;
}) {
  const event = parseSystemEvent(message.body ?? "", names);
  const dot = DOT_TONES[event.tone] ?? "bg-line-strong";
  const attachments = readAttachments(message.attachments);
  const audioAttachments = attachments.filter((item) => item.mime.startsWith("audio/"));

  return (
    <li className="flex items-stretch gap-3 px-2" role="status">
      {/* Dot stays on the title row — not vertically centered when a player sits below. */}
      <span
        className="relative mt-[0.85rem] flex w-1.5 shrink-0 justify-center self-stretch"
        aria-hidden="true"
      >
        {connectTop ? (
          <span className="absolute bottom-full left-1/2 h-[0.85rem] w-px -translate-x-1/2 bg-line" />
        ) : null}
        {connectBottom ? (
          <span className="absolute top-1.5 bottom-0 left-1/2 w-px -translate-x-1/2 bg-line" />
        ) : null}
        <span className={`absolute top-0 size-1.5 rounded-full ring-2 ring-surface ${dot}`} />
      </span>

      <div className="min-w-0 flex-1 py-1.5">
        <div className="flex items-baseline gap-3">
          <p className="min-w-0 flex-1 text-xs leading-snug text-ink-muted">
            <span className="font-medium text-ink">{event.title}</span>
            {event.detail ? <> · {event.detail}</> : null}
          </p>
          <time
            className="tabular shrink-0 text-[10px] text-ink-subtle"
            dateTime={new Date(message.createdAt).toISOString()}
          >
            {formatTime(message.createdAt)}
          </time>
        </div>
        {audioAttachments.length > 0 ? (
          <ul className="mt-1.5 list-none space-y-2 p-0">
            {audioAttachments.map((attachment) => (
              <li key={attachment.id} className="pl-0">
                <AttachmentBlock collabId={collabId} attachment={attachment} mine={false} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

function ChatBubble({
  collabId,
  message,
  mine,
  author,
  showReadReceipts = false,
  read = false,
  showSeenLabel = false,
}: {
  collabId: string;
  message: MessageRow;
  mine: boolean;
  author: string;
  showReadReceipts?: boolean;
  read?: boolean;
  showSeenLabel?: boolean;
}) {
  const attachments = readAttachments(message.attachments);
  const hasBody = Boolean(message.body?.trim());

  return (
    <li className={`flex flex-col py-1.5 ${mine ? "items-end" : "items-start"}`}>
      {/* Inline colors: theme tokens + Tailwind utilities flip on mobile dark mode. */}
      <div
        className={`max-w-[min(85%,28rem)] rounded-2xl px-3.5 py-2 ${
          mine ? "rounded-br-md" : "rounded-bl-md"
        }`}
        style={{
          backgroundColor: "#000000",
          color: "#ffffff",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.14), 0 1px 2px rgba(0,0,0,0.4)",
        }}
      >
        {!mine ? (
          <p className="mb-0.5 text-[11px] font-medium" style={{ color: "rgba(255,255,255,0.55)" }}>
            {author}
          </p>
        ) : null}
        {hasBody ? (
          <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#ffffff" }}>
            {message.body}
          </p>
        ) : null}
        {attachments.length > 0 ? (
          <ul className={`space-y-2 ${hasBody ? "mt-2" : ""}`}>
            {attachments.map((attachment) => (
              <li key={attachment.id}>
                <AttachmentBlock collabId={collabId} attachment={attachment} mine />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-1 flex items-center justify-end gap-1">
          <time
            className="text-[10px] tabular"
            style={{ color: "rgba(255,255,255,0.45)" }}
            dateTime={new Date(message.createdAt).toISOString()}
            title={formatDateTime(message.createdAt)}
          >
            {formatTime(message.createdAt)}
          </time>
          {showReadReceipts ? <ReceiptTicks read={read} /> : null}
        </p>
        {message.redacted ? (
          <p className="mt-1 text-[11px] font-medium" style={{ color: "#fcd34d" }}>
            Contact details hidden
          </p>
        ) : null}
      </div>
      {showSeenLabel ? (
        <p className="mt-0.5 px-1 text-[10px] font-medium tracking-wide text-sky-500/90">Seen</p>
      ) : null}
    </li>
  );
}

/** Thin WhatsApp-style ticks — single = sent, double = seen. */
function ReceiptTicks({ read }: { read: boolean }) {
  const stroke = {
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  return (
    <span
      className="inline-flex shrink-0 self-center"
      style={{ color: read ? "#7dd3fc" : "rgba(255,255,255,0.38)" }}
      title={read ? "Seen" : "Sent"}
      aria-label={read ? "Seen" : "Sent"}
    >
      <svg
        viewBox={read ? "0 0 14 9" : "0 0 10 9"}
        className={read ? "h-[9px] w-[14px]" : "h-[9px] w-[10px]"}
        fill="none"
        aria-hidden="true"
      >
        {read ? (
          <>
            <path d="M0.75 4.6 3.1 7.1 8.35 1.25" {...stroke} />
            <path d="M5.4 7.1 12.85 1.25" {...stroke} />
          </>
        ) : (
          <path d="M0.75 4.6 3.1 7.1 8.85 1.25" {...stroke} />
        )}
      </svg>
    </span>
  );
}

type MessageAttachmentMeta = {
  id: string;
  name: string;
  mime: string;
  bytes: number;
};

function readAttachments(raw: unknown): MessageAttachmentMeta[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is MessageAttachmentMeta =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as MessageAttachmentMeta).id === "string" &&
      typeof (item as MessageAttachmentMeta).name === "string" &&
      typeof (item as MessageAttachmentMeta).mime === "string",
  );
}

function AttachmentBlock({
  collabId,
  attachment,
  mine,
}: {
  collabId: string;
  attachment: MessageAttachmentMeta;
  mine: boolean;
}) {
  const href = `${publicApiUrl()}/api/collabs/${collabId}/messages/${attachment.id}/file`;
  const isImage = attachment.mime.startsWith("image/");
  const isAudio = attachment.mime.startsWith("audio/");

  if (isImage) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`block overflow-hidden rounded-lg ring-1 ${
          mine ? "ring-white/20" : "ring-line/70"
        }`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={href}
          alt={attachment.name}
          loading="lazy"
          decoding="async"
          className="max-h-48 w-full bg-black/5 object-cover"
        />
      </a>
    );
  }

  if (isAudio) {
    // Native controls only — flush left with the activity title above.
    return (
      <audio
        controls
        preload="metadata"
        src={href}
        aria-label={attachment.name || "Call recording"}
        className="m-0 block h-9 w-full max-w-sm align-middle"
      />
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs ring-1 ${
        mine ? "bg-white/10 text-white ring-white/20" : "bg-canvas text-ink ring-line/70"
      }`}
    >
      <span
        className={`inline-flex size-7 shrink-0 items-center justify-center rounded-md text-[9px] font-bold ${
          mine ? "bg-white/15 text-white" : "bg-surface text-ink-muted ring-1 ring-line"
        }`}
      >
        {fileBadge(attachment.mime)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{attachment.name}</span>
        <span className={`block truncate ${mine ? "text-white/50" : "text-ink-subtle"}`}>
          {formatFileSize(attachment.bytes)}
        </span>
      </span>
    </a>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileBadge(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return "IMG";
  if (mime.startsWith("audio/")) return "AUD";
  return "FILE";
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}
